<template>
  <!-- 2026-10-13：独立提问窗口只显示问题和GPT文字回复，入口与发送允许平台和分店启用后台账号。 -->
  <span v-if="allowed" class="gpt-question-control">
    <slot :open-panel="openPanel" />
    <a-modal
      v-model:visible="visible"
      title="提问"
      :footer="null"
      :width="760"
      :style="{ maxWidth: 'calc(100vw - 24px)', top: '24px' }"
      :destroy-on-close="true"
    >
      <div class="questions-panel">
        <div class="questions-status">
          <a-tag :color="online ? 'green' : 'default'">{{ online ? 'GPT在线' : 'GPT离线' }}</a-tag>
          <span class="retention-label">最近{{ retentionDays }}天</span>
          <a-button type="text" size="small" title="刷新" aria-label="刷新" :loading="loading" :disabled="sending" @click="refreshQuestions">
            <template #icon><ReloadOutlined /></template>
          </a-button>
        </div>
        <a-alert v-if="loadError" class="question-alert" type="error" :message="loadError" show-icon />
        <a-alert v-else-if="loaded && !online" class="question-alert" type="warning" message="GPT当前离线，问题将等待回复" show-icon />
        <a-alert v-if="submitError" class="question-alert" type="error" show-icon>
          <template #message>
            <div class="submission-error"><span>{{ submitError }}</span><a-button v-if="retryAttempt" size="small" title="重试提交" :disabled="!canRetry" @click="retrySubmission"><template #icon><ReloadOutlined /></template>重试</a-button></div>
          </template>
        </a-alert>
        <div ref="conversation" class="question-history" aria-live="polite" :aria-busy="loading">
          <a-spin v-if="loading" class="history-loading" />
          <a-empty v-else-if="!messages.length && loaded" description="暂无提问" />
          <section v-for="question in messages" :key="question.id" class="question-turn">
            <div class="turn-label">我</div>
            <div class="question-text">{{ question.text }}</div>
            <div class="answer-label">GPT</div>
            <div v-if="question.answer" class="answer-text">{{ question.answer }}</div>
            <div v-if="question.status === 'pending'" class="answer-status">等待回复</div>
            <div v-else-if="question.status === 'answering'" class="answer-status"><LoadingOutlined />正在回答</div>
            <div v-else-if="question.status === 'failed'" class="failed-answer"><span>回复失败</span><a-button size="small" title="重新提问" :disabled="!canRetry" @click="retryQuestion(question)"><template #icon><ReloadOutlined /></template>重试</a-button></div>
          </section>
        </div>
        <div class="question-composer">
          <a-textarea v-model:value="draft" :maxlength="4000" :rows="3" :disabled="sending" placeholder="请输入问题" aria-label="问题" show-count />
          <div class="composer-actions">
            <span v-if="hasActiveQuestion" class="answer-status">{{ activeStatus === 'answering' ? '正在回答' : '等待回复' }}</span>
            <a-button type="primary" title="发送问题" :loading="sending" :disabled="!canSubmit" @click="submitQuestion"><template #icon><SendOutlined /></template>发送</a-button>
          </div>
        </div>
      </div>
    </a-modal>
  </span>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { LoadingOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons-vue'
import store from '@/store'
import questionsRequest, { GptQuestion, GptQuestionsSnapshot } from '@/api/gptQuestions'
import { canAskGptQuestions } from '@/utils/gptQuestionAccess'

interface QuestionSubmission { text: string; requestId: string; fromDraft: boolean }

// 2026-10-13：轮询与提交串行，窗口关闭和身份变化使旧请求失效；网络重试复用请求ID。
export default defineComponent({
  name: 'GptQuestionPanel',
  components: { LoadingOutlined, ReloadOutlined, SendOutlined },
  setup() {
    // 2026-10-18：每次打开和提交均校验独立VIP问答权限，撤权结束轮询并取消旧请求。
    const allowed = computed(() => canAskGptQuestions(store.getters.token, store.getters.userInfo, store.getters.sysFunctions))
    const identity = computed(() => {
      const user = store.getters.userInfo || {}
      // 2026-10-13：账号或角色停用同步关闭问答并清理旧会话，迟到回复继续按原身份丢弃。
      return JSON.stringify([store.getters.token, user.id, user.orgId, user.roleId, user.disable, user.roleStatus, allowed.value])
    })
    const visible = ref(false), loading = ref(false), loaded = ref(false), sending = ref(false), online = ref(false)
    const messages = ref<GptQuestion[]>([]), draft = ref(''), loadError = ref(''), submitError = ref('')
    const retentionDays = ref(30), retryAttempt = ref<QuestionSubmission | null>(null)
    const conversation = ref<HTMLElement | null>(null)
    const activeStatus = computed(() => messages.value.find(item => item.status === 'pending' || item.status === 'answering')?.status)
    const hasActiveQuestion = computed(() => !!activeStatus.value)
    const canRetry = computed(() => allowed.value && visible.value && loaded.value && !sending.value && !hasActiveQuestion.value)
    const canSubmit = computed(() => canRetry.value && !!draft.value.trim() && draft.value.length <= 4000)
    let alive = true, generation = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    let currentRead: Promise<void> | null = null
    let controller: AbortController | null = null

    const current = (version: number, actor: string) => alive && visible.value && allowed.value && version === generation && actor === identity.value
    const clearTimer = () => { if (timer !== undefined) clearTimeout(timer); timer = undefined }
    const stopRequests = () => {
      generation++
      clearTimer()
      controller?.abort()
      controller = null
      currentRead = null
      loading.value = false
      sending.value = false
    }
    const errorText = (error: unknown, fallback: string) =>
      typeof (error as { message?: unknown })?.message === 'string' ? (error as { message: string }).message : fallback

    const confirmSubmission = (submission: QuestionSubmission) => {
      if (submission.fromDraft && draft.value.trim() === submission.text) draft.value = ''
      retryAttempt.value = null
      submitError.value = ''
    }
    const applySnapshot = (snapshot: GptQuestionsSnapshot) => {
      const element = conversation.value
      const follow = !element || element.scrollHeight - element.scrollTop - element.clientHeight < 72
      messages.value = snapshot.messages
      online.value = snapshot.online
      retentionDays.value = snapshot.retentionDays || 30
      loaded.value = true
      loadError.value = ''
      const attempt = retryAttempt.value
      if (attempt && snapshot.messages.some(item => item.id === attempt.requestId)) confirmSubmission(attempt)
      if (follow) void nextTick(() => { if (conversation.value) conversation.value.scrollTop = conversation.value.scrollHeight })
    }
    const scheduleRefresh = () => {
      clearTimer()
      if (alive && visible.value && allowed.value && !sending.value) timer = setTimeout(() => { timer = undefined; void refreshQuestions() }, 2000)
    }
    const refreshQuestions = (): Promise<void> => {
      if (!alive || !visible.value || !allowed.value || sending.value) return Promise.resolve()
      if (currentRead) return currentRead
      clearTimer()
      const version = generation, actor = identity.value, requestController = new AbortController()
      controller = requestController
      loading.value = !loaded.value
      currentRead = (async () => {
        try {
          const snapshot = await questionsRequest.list(requestController.signal)
          if (current(version, actor)) applySnapshot(snapshot)
        } catch (error) {
          if (current(version, actor)) loadError.value = errorText(error, '提问记录加载失败，请重试。')
        } finally {
          if (current(version, actor)) {
            loading.value = false
            currentRead = null
            controller = null
            scheduleRefresh()
          }
        }
      })()
      return currentRead
    }
    const requestId = () => {
      const secureCrypto = crypto as Crypto & { randomUUID?: () => string }
      if (typeof secureCrypto.randomUUID === 'function') return secureCrypto.randomUUID()
      const bytes = crypto.getRandomValues(new Uint8Array(16))
      bytes[6] = (bytes[6] & 15) | 64
      bytes[8] = (bytes[8] & 63) | 128
      const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }
    const postQuestion = async (submission: QuestionSubmission) => {
      if (!allowed.value || !visible.value || !loaded.value || sending.value) return
      if (!submission.text || submission.text.length > 4000) return
      const version = generation, actor = identity.value
      sending.value = true
      clearTimer()
      await currentRead
      if (!current(version, actor)) return
      if (hasActiveQuestion.value) { sending.value = false; scheduleRefresh(); return }
      const requestController = new AbortController()
      controller = requestController
      retryAttempt.value = submission
      submitError.value = ''
      try {
        const snapshot = await questionsRequest.ask(submission.text, submission.requestId, requestController.signal)
        if (current(version, actor)) {
          applySnapshot(snapshot)
          confirmSubmission(submission)
        }
      } catch (error) {
        if (current(version, actor)) submitError.value = errorText(error, '问题提交失败，请重试。')
      } finally {
        if (current(version, actor)) {
          sending.value = false
          controller = null
          scheduleRefresh()
        }
      }
    }
    const submitQuestion = () => {
      if (!canSubmit.value) return Promise.resolve()
      const text = draft.value.trim(), attempt = retryAttempt.value
      return postQuestion(attempt?.fromDraft && attempt.text === text ? attempt : { text, requestId: requestId(), fromDraft: true })
    }
    const retrySubmission = () => retryAttempt.value && canRetry.value ? postQuestion(retryAttempt.value) : Promise.resolve()
    const retryQuestion = (question: GptQuestion) => question.status === 'failed' && canRetry.value
      ? postQuestion({ text: question.text, requestId: requestId(), fromDraft: false }) : Promise.resolve()
    const openPanel = async () => {
      if (!alive || !allowed.value || visible.value) return
      stopRequests()
      loaded.value = false
      online.value = false
      loadError.value = ''
      visible.value = true
      await refreshQuestions()
    }

    watch(visible, value => { if (!value) stopRequests() }, { flush: 'sync' })
    watch(identity, () => {
      visible.value = false
      stopRequests()
      messages.value = []
      draft.value = ''
      loadError.value = ''
      submitError.value = ''
      retryAttempt.value = null
      loaded.value = false
      online.value = false
    }, { flush: 'sync' })
    onBeforeUnmount(() => { alive = false; stopRequests() })
    return {
      allowed, visible, loading, loaded, sending, online, messages, draft, loadError, submitError, retentionDays,
      retryAttempt, conversation, activeStatus, hasActiveQuestion, canRetry, canSubmit,
      openPanel, refreshQuestions, submitQuestion, retrySubmission, retryQuestion
    }
  }
})
</script>

<style lang="scss" scoped>
/* 2026-10-13：问答窗口限制高度并独立滚动，长文字与窄屏保持完整可读。 */
.gpt-question-control { display: inline-flex; align-items: center; }
.questions-panel { display: flex; flex-direction: column; height: min(640px, calc(100vh - 180px)); min-height: 260px; }
.questions-status { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; flex-shrink: 0; }
.questions-status .ant-tag { margin-right: 0; }
.questions-status .ant-btn { margin-left: auto; }
.retention-label { font-size: 12px; color: #767676; }
.question-alert { margin-bottom: 10px; flex-shrink: 0; }
.submission-error { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.submission-error span { min-width: 0; overflow-wrap: anywhere; }
.submission-error .ant-btn { flex-shrink: 0; }
.question-history { flex: 1; min-height: 100px; overflow-y: auto; padding: 0 4px 12px; }
.history-loading { display: block; padding: 50px 0; }
.question-turn { padding: 16px 0; border-top: 1px solid #e8e8e8; }
.question-turn:first-of-type { border-top: 0; padding-top: 4px; }
.turn-label, .answer-label { font-size: 12px; font-weight: 600; color: #666; margin-bottom: 5px; }
.answer-label { margin-top: 14px; color: #24774d; }
.question-text, .answer-text { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.65; }
.question-text { color: #333; }
.answer-text { color: #222; }
.answer-status { color: #767676; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.failed-answer { display: flex; align-items: center; gap: 12px; color: #b53c32; margin-top: 6px; }
.question-composer { border-top: 1px solid #e8e8e8; padding-top: 12px; flex-shrink: 0; }
.composer-actions { display: flex; align-items: center; gap: 12px; margin-top: 24px; }
.composer-actions .ant-btn { margin-left: auto; }
@media (max-width: 600px) {
  .questions-panel { height: calc(100vh - 180px); min-height: 220px; }
  .question-history { padding-right: 2px; }
}
</style>
